import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import FHIR from 'fhirclient';
import { smartReady } from './smartReady';
import { resolveRole } from './resolveRole';
import { useSessionStore } from '../stores/sessionStore';

/**
 * ============================================================================
 * AuthProvider — Dual-Mode Authentication for CollabKare TeleVisit
 * ============================================================================
 *
 * TV-RV-02/04 (2026-06-21): role resolution is now fully delegated to the
 * central `resolveRole()` module (src/auth/resolveRole.js). The old
 * `extractKeycloakRole()` function and the inline EHR-mode ternary have been
 * deleted. All three auth paths (EHR-launch callback resume, standalone KC,
 * dev bypass) now call `resolveRole({ fhirUser, kcClaims, launchHint })`.
 *
 * This app supports THREE authentication modes:
 *
 * 1. EHR LAUNCH (Smart on FHIR)
 *    - Triggered when launched from an EHR with /launch?iss=...&launch=...
 *    - Uses the fhirclient library (FHIR.oauth2.authorize / ready)
 *    - Auth server is the EHR's own authorization server (Epic, Cerner, etc.)
 *    - Patient + encounter context comes from the EHR's launch token
 *    - FHIR calls use the EHR-issued access token
 *
 * 2. STANDALONE LAUNCH (Keycloak)
 *    - Triggered when user opens the app directly (e.g., from a portal link)
 *    - Uses Keycloak as CollabKare's own OIDC provider
 *    - Patient/encounter context comes from URL params or Keycloak token claims
 *    - FHIR calls use a Keycloak-issued token trusted by our HAPI FHIR Server
 *
 * 3. DEV BYPASS
 *    - Triggered when VITE_AUTH_BYPASS=true or no Keycloak URL is configured
 *    - Skips all auth, provides mock user and SMART context
 *    - For local UI development without infrastructure
 *
 * The rest of the app doesn't need to know which mode is active — it just
 * calls useAuth() and gets: user, token, smartContext, fhirClient.
 * ============================================================================
 */

const AuthContext = createContext(null);

// ── Dev Bypass ──────────────────────────────────────────────────────────────
const DEV_BYPASS = import.meta.env.DEV && (
  import.meta.env.VITE_AUTH_BYPASS === 'true' ||
  !import.meta.env.VITE_KEYCLOAK_URL
);

const MOCK_USER = {
  id: 'dev-provider-001',
  name: 'Dr. Asha Reddy',
  email: 'dr.reddy@collabkare.io',
  role: 'provider',
  fhirUser: 'Practitioner/dev-provider-001',
};

const MOCK_SMART_CONTEXT = {
  patientId: 'patient-maria-johnson',
  encounterId: 'enc-001',
  fhirServer: 'https://r4.collabkare.io/dev-tenant',
  fhirUser: 'Practitioner/dev-provider-001',
  authMode: 'dev',
};

// ── AuthProvider Component ──────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [authMode, setAuthMode] = useState(DEV_BYPASS ? 'dev' : null); // 'ehr' | 'standalone' | 'dev'
  const [isLoading, setIsLoading] = useState(!DEV_BYPASS);
  const [isAuthenticated, setIsAuthenticated] = useState(DEV_BYPASS);
  const [user, setUser] = useState(DEV_BYPASS ? MOCK_USER : null);
  const [token, setToken] = useState(DEV_BYPASS ? 'dev-mock-token' : null);
  const [smartContext, setSMARTContext] = useState(
    DEV_BYPASS ? MOCK_SMART_CONTEXT : {
      patientId: null,
      encounterId: null,
      fhirServer: null,
      fhirUser: null,
      authMode: null,
    }
  );
  // The fhirclient Client instance (only set for EHR launch mode)
  const [fhirClient, setFhirClient] = useState(null);
  const keycloakRef = useRef(null);
  // Guard against React StrictMode double-mount in dev. Without this, kc.init()
  // runs twice → second run rewrites PKCE code_verifier in sessionStorage →
  // KC issues code keyed to first challenge → keycloak-js verifies with second
  // verifier → fails silently → onLoad:'login-required' loops forever.
  // See HAR analysis 2026-05-28: 35 auth round-trips in 10s.
  const initStartedRef = useRef(false);

  // ── Determine auth mode and initialize ──────────────────────────────────
  useEffect(() => {
    if (DEV_BYPASS) {
      console.log('[Auth] Dev bypass active — using mock provider auth.');
      return;
    }
    if (initStartedRef.current) {
      console.log('[Auth] init already in flight (StrictMode double-mount) — skipping');
      return;
    }
    initStartedRef.current = true;

    const init = async () => {
      try {
        // Check if we're returning from an EHR launch (fhirclient stores state in sessionStorage)
        // or if SmartCallback already set authMode = 'ehr'
        const storedMode = sessionStorage.getItem('authMode');

        if (storedMode === 'ehr') {
          await initEHRMode();
        } else {
          // Check if fhirclient has a pending authorization (mid-launch)
          try {
            const client = await smartReady();
            if (client) {
              sessionStorage.setItem('authMode', 'ehr');
              await initEHRModeWithClient(client);
              return;
            }
          } catch {
            // No active SMART session — fall through to standalone
          }
          await initStandaloneMode();
        }
      } catch (err) {
        console.error('[Auth] Initialization failed:', err);
        setIsLoading(false);
      }
    };

    init();
  }, []);

  // ── EHR Launch Mode ─────────────────────────────────────────────────────
  const initEHRMode = async () => {
    try {
      const client = await smartReady();
      await initEHRModeWithClient(client);
    } catch (err) {
      console.error('[Auth] EHR mode failed, falling back to standalone:', err);
      sessionStorage.removeItem('authMode');
      await initStandaloneMode();
    }
  };

  const initEHRModeWithClient = async (client) => {
    setAuthMode('ehr');
    setFhirClient(client);

    const tokenResponse = client.state?.tokenResponse || {};
    const accessToken = tokenResponse.access_token || client.state?.tokenResponse?.access_token;
    setToken(accessToken);

    // CollabKare KC SMART SPI writes `patient` (and `encounter`) into the
    // access-token JWT claims correctly, but NOT yet into the top-level
    // /token response wrapper. fhirclient's `client.patient.id` only reads
    // the top-level wrapper, so we decode the JWT here and fall through.
    // Tracked separately; safe long-term — if KC starts emitting the
    // top-level field, `tokenResponse.patient` wins and the JWT fallback
    // is a no-op.
    const atClaims = decodeJwtClaims(accessToken);

    // Build user from fhirUser reference
    const fhirUser = client.getFhirUser?.() || tokenResponse.fhirUser || '';
    // STAB POLISH-3 — read display name from KC ID-token claims rather
    // than doing a FHIR fetch on fhirUser. Three reasons:
    //   1. The fhirUser claim in the access token still carries the
    //      master id (e.g. Patient/-48) due to a KC realm mapper
    //      collision tracked as STAB-03-followup #211 — that resource
    //      doesn't exist in non-primary partitions, so the fetch 404s.
    //   2. Even when fhirUser is correct, fhirclient's request method
    //      can construct doubled URLs like /Patient/Patient/-45 →
    //      400. Removing the call avoids both.
    //   3. KC's ID token always carries given_name + family_name +
    //      preferred_username. Those are populated at registration,
    //      stable, and zero-cost to read.
    let userName =
      [atClaims.given_name, atClaims.family_name].filter(Boolean).join(' ') ||
      atClaims.preferred_username ||
      tokenResponse.email ||
      'Unknown User';

    // TV-RV-04 — Use central resolver with KC roles + fhirUser fallback.
    // launchHint is not available here (we're restoring from sessionStorage
    // after the callback already ran); the session store's userRole was set
    // by SmartCallback with the hint. We pass empty hint so role-match
    // ordering determines the default (front-desk before provider).
    const role = resolveRole({ fhirUser, kcClaims: atClaims, launchHint: '' });

    setUser({
      id: fhirUser.split('/')[1] || tokenResponse.sub,
      // STAB-04 (2026-06-03) — explicit KC sub for any caller that needs
      // to match `request.auth.uid` in Firestore rules. Used to fall out
      // accidentally via the `|| tokenResponse.sub` fallback in `id`,
      // which broke once STAB-03 started populating fhirUser correctly.
      // Always prefer atClaims.sub (the access token's sub) since that's
      // what exchangeKeycloakToken uses as Firebase Auth uid.
      sub: atClaims.sub || tokenResponse.sub || null,
      name: userName,
      email: tokenResponse.email || '',
      role,
      fhirUser,
    });

    setSMARTContext({
      patientId: client.patient?.id || tokenResponse.patient || atClaims.patient || null,
      encounterId: client.encounter?.id || tokenResponse.encounter || atClaims.encounter || null,
      fhirServer: client.state?.serverUrl || '',
      fhirUser,
      authMode: 'ehr',
    });

    setIsAuthenticated(true);
    setIsLoading(false);
    console.log('[Auth] EHR launch mode active. FHIR server:', client.state?.serverUrl);
  };

  // ── Standalone Mode (Keycloak) ──────────────────────────────────────────
  const initStandaloneMode = async () => {
    setAuthMode('standalone');

    try {
      const { default: Keycloak } = await import('keycloak-js');

      const kc = new Keycloak({
        url: import.meta.env.VITE_KEYCLOAK_URL,
        realm: import.meta.env.VITE_KEYCLOAK_REALM || 'CollabKare',
        clientId: import.meta.env.VITE_KEYCLOAK_CLIENT_ID || 'collabkare-televisit',
      });

      const authenticated = await kc.init({
        onLoad: 'login-required',
        flow: 'standard',
        checkLoginIframe: false,
        enableLogging: import.meta.env.DEV,
        pkceMethod: 'S256', // PKCE for standalone too
      });

      keycloakRef.current = kc;

      if (authenticated) {
        const profile = kc.tokenParsed || {};

        // TV-RV-04 — Standalone: pass the full parsed token as kcClaims.
        // Read ?role= from current URL as the launchHint (a standalone
        // launch from a portal can still pass ?role=provider).
        const launchHint = new URLSearchParams(window.location.search).get('role') || '';
        const fhirUserRaw = profile.fhirUser || '';

        // TV-RV-04 — Use central resolver for standalone mode too.
        const role = resolveRole({ fhirUser: fhirUserRaw, kcClaims: profile, launchHint });

        // R2-8 — synthesize the fhirUser prefix from the RESOLVED role.
        // The old ternary only special-cased 'provider', so an org admin
        // (or any product-coined provider role the extractor missed) got
        // labeled Patient/<sub> — which then drove the provider queue
        // with a Patient reference. Practitioner for any staff role.
        const synthPrefix = role === 'patient' ? 'Patient' : 'Practitioner';
        const fhirUser = fhirUserRaw || `${synthPrefix}/${profile.sub}`;

        // TV-P0-04 + R2-8 — Tenant routing
        // ────────────────────────────────────────────────────────────────
        // Two distinct user populations land here:
        //
        //   B2C PATIENTS — `patient_tenants` (array) carries the REAL FHIR
        //   partition IDs the patient has data in (e.g.
        //   ["kare_collabkare_default","kare_demo_provider"]). Their
        //   `tenant_id`-style claim is a billing label, NOT a partition.
        //
        //   ORG STAFF (providers/admins) — their JWT `tenantId` claim IS
        //   the org's HAPI partition (e.g. ramesh_hospitals_cardiology_
        //   brodipet) and they have NO patient_tenants claim. e2e Round 2
        //   (2026-06-11) found a provider silently routed to the
        //   kare_collabkare_default fallback because only patient_tenants
        //   was consulted → empty queue in the wrong tenant.
        //
        // Resolution order therefore depends on role (computed above):
        //   staff:    tenantId claim → patient_tenants[0] → env default
        //   patient:  patient_tenants[0] → tenantId claim → env default
        const patientTenants = Array.isArray(profile.patient_tenants)
          ? profile.patient_tenants
          : (profile.patient_tenants ? [profile.patient_tenants] : []);
        const staffTenant = profile.tenantId || profile.tenant_id_partition || null;
        const isStaff = role === 'provider' || role === 'admin' || role === 'front-desk';
        const fhirTenantId =
          (isStaff
            ? (staffTenant || patientTenants[0])
            : (patientTenants[0] || staffTenant))
          || import.meta.env.VITE_DEFAULT_TENANT_ID
          || 'kare_collabkare_default';
        if (!staffTenant && patientTenants.length === 0) {
          console.warn('[Auth] No tenantId/patient_tenants claim — FHIR calls will use default tenant', fhirTenantId);
        } else if (patientTenants.length > 1) {
          console.log(`[Auth] patient_tenants has ${patientTenants.length} entries — using first (${fhirTenantId}); future TV ticket: tenant picker`);
        }

        // SMART patient id — KC mapper uses claim name `patient` (not `patient_id`)
        const smartPatientId = profile.patient || profile.patient_id || null;

        console.log('[Auth] Keycloak authenticated', {
          azp: profile.azp,
          fhirUser: profile.fhirUser ?? '(synth)',
          role,
          patient: smartPatientId ?? '(none)',
          fhirTenantId,
          patientTenants,
        });

        setToken(kc.token);
        setUser({
          id: profile.sub,
          name: profile.name || profile.preferred_username,
          email: profile.email,
          role,
          fhirUser,
        });
        setSMARTContext({
          patientId: smartPatientId,
          encounterId: profile.encounter || profile.encounter_id || null,
          fhirServer: import.meta.env.VITE_FHIR_BASE_URL || 'https://r4.collabkare.io',
          fhirUser,
          authMode: 'standalone',
        });

        // Push to the zustand session store so fhir hooks pick up tenantId.
        // (AuthProvider's local smartContext state is for context consumers;
        // the fhir client reads tenantId from useSessionStore.)
        const { setTenantId, setSMARTContext: setStoreSmart,
                setCurrentPatient, setUserRole } = useSessionStore.getState();
        setTenantId(fhirTenantId);
        setStoreSmart({
          patientId: smartPatientId,
          encounterId: profile.encounter || profile.encounter_id || null,
          fhirServer: import.meta.env.VITE_FHIR_BASE_URL || 'https://r4.collabkare.io',
          fhirUser,
          authMode: 'standalone',
          patientTenants, // full list for future picker
        });
        setCurrentPatient(smartPatientId);
        setUserRole(role);

        setIsAuthenticated(true);
      } else {
        // R2-8 — not authenticated: nuke any persisted session context so
        // a previous user's tenant/role/fhirUser can't drive the UI.
        console.warn('[Auth] KC init returned unauthenticated — clearing persisted session context');
        useSessionStore.getState().clearSession();
      }
    } catch (err) {
      console.error('[Auth] Keycloak initialization failed:', err);
      // R2-8 — same guard on hard failure. Without this, the zustand-
      // persisted televisit-session (sessionStorage) kept serving stale
      // tenantId/userRole/fhirUser from a prior visit and the queue
      // rendered against the wrong tenant with no token at all.
      useSessionStore.getState().clearSession();
    } finally {
      setIsLoading(false);
    }
  };

  // ── Keycloak Token Refresh ──────────────────────────────────────────────
  useEffect(() => {
    const kc = keycloakRef.current;
    if (!kc || !isAuthenticated || authMode !== 'standalone') return;

    const interval = setInterval(() => {
      kc.updateToken(300)
        .then((refreshed) => {
          if (refreshed) {
            setToken(kc.token);
            console.log('[Auth] Keycloak token refreshed');
          }
        })
        .catch(() => {
          console.warn('[Auth] Token refresh failed, logging out');
          kc.logout();
        });
    }, 60000);

    return () => clearInterval(interval);
  }, [isAuthenticated, authMode]);

  // ── EHR Token Refresh ───────────────────────────────────────────────────
  useEffect(() => {
    if (authMode !== 'ehr' || !fhirClient) return;

    // fhirclient handles token refresh internally when using client.request()
    // but we keep our token state in sync for direct FHIR client usage
    const interval = setInterval(async () => {
      try {
        const client = await smartReady();
        const newToken = client.state?.tokenResponse?.access_token;
        if (newToken && newToken !== token) {
          setToken(newToken);
          setFhirClient(client);
          console.log('[Auth] EHR token refreshed');
        }
      } catch {
        console.warn('[Auth] EHR token refresh check failed');
      }
    }, 120000); // every 2 minutes

    return () => clearInterval(interval);
  }, [authMode, fhirClient, token]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const login = useCallback(async (options = {}) => {
    if (DEV_BYPASS) return;
    if (authMode === 'standalone' && keycloakRef.current) {
      await keycloakRef.current.login(options);
    }
    // EHR mode: login is handled by the EHR, not by us
  }, [authMode]);

  const logout = useCallback(async () => {
    if (DEV_BYPASS) {
      console.log('[Auth] Dev mode — logout is a no-op.');
      return;
    }

    // Clear SMART session
    sessionStorage.removeItem('authMode');
    sessionStorage.removeItem('SMART_KEY');

    if (authMode === 'standalone' && keycloakRef.current) {
      await keycloakRef.current.logout();
    } else if (authMode === 'ehr') {
      // For EHR launch, we can't log out of the EHR — just clear local state
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
      setFhirClient(null);
      window.location.href = '/';
    }
  }, [authMode]);

  // ── Context Value ───────────────────────────────────────────────────────
  const value = {
    // State
    authMode,            // 'ehr' | 'standalone' | 'dev'
    isAuthenticated,
    isLoading,
    user,                // { id, name, email, role, fhirUser }
    token,               // Raw access token (EHR-issued or Keycloak-issued)
    smartContext,         // { patientId, encounterId, fhirServer, fhirUser, authMode }
    fhirClient,          // fhirclient Client instance (EHR mode only, null otherwise)

    // Actions
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ── useAuth Hook ────────────────────────────────────────────────────────────
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Decode JWT payload without signature verification.
 * Used in EHR mode to read KC realm_access/resource_access claims.
 */
function decodeJwtClaims(jwt) {
  try {
    const seg = jwt?.split('.')?.[1];
    if (!seg) return {};
    const padded = seg + '='.repeat((4 - (seg.length % 4)) % 4);
    return JSON.parse(atob(padded.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
}
